import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { useFirebasePhoneAuth } from '../../hooks/useFirebasePhoneAuth'
import { formatCooldown } from '../../utils/cooldown'
import { AuthHeroLayout } from '../../components/auth/AuthHeroLayout'
import { PhoneInput } from '../../components/auth/PhoneInput'
import { PasswordInput } from '../../components/auth/PasswordInput'
import { prepareForSubmit, type CountryCode } from '@jnati/phone'
import { getErrorMessage } from '../../utils/errorMessage'

// Mirrors apps/mobile/app/(auth)/forgot-password.tsx — 3-step wizard:
//   1. enter phone → Firebase signInWithPhoneNumber (invisible reCAPTCHA)
//   2. enter OTP → confirmation.confirm → Firebase ID token
//   3. enter new password → POST /api/auth/reset-password
// Hero + card composition aligned with jnati.com marketing in #7.

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-center">
      {[1, 2, 3].map((s, i) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
              step >= s
                ? 'border-gold bg-gold text-navy'
                : 'border-white/30 bg-white/10 text-white/70'
            }`}
          >
            {s}
          </div>
          {i < 2 && (
            <div className={`w-8 h-0.5 ${step > s ? 'bg-gold' : 'bg-white/20'}`} />
          )}
        </div>
      ))}
    </div>
  )
}

// Auth-card focal-point button — gold per #7 (matches Login).
const goldBtn = 'w-full bg-gold text-navy rounded-lg py-3 mt-5 font-bold hover:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed transition'

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  // #phone-auth-unify: hook owns OTP send/verify + reCAPTCHA lifecycle
  // + error mapping. Closes the two mapper-coverage gaps flagged in
  // 6bdf5af — error messages here now come from the same mapper used
  // by Signup + AddPhonePrompt instead of raw Firebase strings or the
  // hardcoded 'Invalid OTP. Try again.'.
  const { state: otpState, sendOtp, verifyOtp, reset: resetOtp } = useFirebasePhoneAuth()

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [phone, setPhone] = useState('')
  // #intl-auth-1c: default India. Non-India blocked in the hook (coming soon).
  const [country, setCountry] = useState<CountryCode>('IN')
  const [otp, setOtp] = useState('')
  const [firebaseToken, setFirebaseToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [apiLoading, setApiLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendSeconds, setResendSeconds] = useState(0)

  const sending = otpState.status === 'sending'
  const verifying = otpState.status === 'verifying'
  // #otp-cooldown-ui: rate-limit countdown.
  const cooldownMs = otpState.cooldownMsRemaining ?? 0
  const isCooldown = cooldownMs > 0

  useEffect(() => {
    if (resendSeconds <= 0) return
    const t = setTimeout(() => setResendSeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [resendSeconds])

  const startResendTimer = () => setResendSeconds(60)

  async function trySendOtp(): Promise<boolean> {
    setError('')
    try {
      // #forgot-password-semantic-invert: intent='forgot-password'
      // tells the hook to invert the precheck — 200 (phone free)
      // blocks with "no account exists"; 409 (phone taken) is the
      // success branch and proceeds to send the SMS.
      await sendOtp(phone, { intent: 'forgot-password', country })
      return true
    } catch (err) {
      setError(otpState.error ?? getErrorMessage(err, 'Failed to send OTP. Try again.'))
      return false
    }
  }

  async function handleSendOtp(e: FormEvent) {
    e.preventDefault()
    const ok = await trySendOtp()
    if (ok) {
      setStep(2)
      startResendTimer()
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault()
    if (otp.length < 6) {
      setError('Enter the 6-digit code')
      return
    }
    setError('')
    try {
      const token = await verifyOtp(otp)
      setFirebaseToken(token)
      setStep(3)
    } catch (err) {
      setError(otpState.error ?? getErrorMessage(err, 'Invalid OTP. Try again.'))
    }
  }

  async function handleResend() {
    if (resendSeconds > 0) return
    const ok = await trySendOtp()
    if (ok) startResendTimer()
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault()
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    // #e164-migration Release C: send the E.164 value; backend dual-read
    // finds the stored E.164 row. OTP already verified this phone/country.
    const prep = prepareForSubmit(phone, country)
    if (!prep.ok) { setError(prep.message); return }
    setError('')
    setApiLoading(true)
    try {
      await apiClient.post('/api/auth/reset-password', {
        phoneNumber: prep.e164,
        firebaseToken,
        newPassword,
      })
      resetOtp()
      navigate('/login', { replace: true })
    } catch (err) {
      setError(getErrorMessage(err, 'Reset failed'))
    } finally {
      setApiLoading(false)
    }
  }

  return (
    <AuthHeroLayout
      hideTagline
      belowLogo={<StepIndicator step={step} />}
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="text-gold font-bold hover:underline">Back to Login</Link>
        </>
      }
    >
      {error && <p className="text-danger text-sm mb-3" role="alert">{error}</p>}

      {step === 1 && (
        <form onSubmit={handleSendOtp}>
          <h2 className="text-navy text-xl font-extrabold">Reset your password</h2>
          <p className="text-gray-500 text-sm mt-1 mb-4">Enter your registered phone number</p>
          <PhoneInput value={phone} onChange={setPhone} country={country} onCountryChange={setCountry} disabled={sending || verifying || apiLoading} topless />
          <button type="submit" disabled={sending || verifying || apiLoading || isCooldown} className={goldBtn}>
            {sending
              ? 'Sending OTP…'
              : isCooldown
                ? `Try again in ${formatCooldown(cooldownMs)}`
                : 'Send OTP'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleVerifyOtp}>
          <h2 className="text-navy text-xl font-extrabold">Verify your number</h2>
          <p className="text-gray-500 text-sm mt-1 mb-4">Code sent to +91 {phone}</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full text-center text-2xl tracking-widest border-2 border-border rounded-lg py-3 text-navy outline-none"
          />
          <button type="submit" disabled={sending || verifying || apiLoading || otp.length < 6} className={goldBtn}>
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendSeconds > 0 || sending || verifying || isCooldown}
            className="w-full text-navy text-sm font-semibold mt-3 hover:underline disabled:opacity-40 disabled:hover:no-underline"
          >
            {isCooldown
              ? `Try again in ${formatCooldown(cooldownMs)}`
              : resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : 'Resend OTP'}
          </button>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handleResetPassword}>
          <h2 className="text-navy text-xl font-extrabold mb-4">Set new password</h2>
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            label="New Password"
            placeholder="New password"
            autoComplete="new-password"
            disabled={sending || verifying || apiLoading}
            topless
          />
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            label="Confirm Password"
            placeholder="Confirm password"
            autoComplete="new-password"
            disabled={sending || verifying || apiLoading}
          />
          <button type="submit" disabled={sending || verifying || apiLoading} className={goldBtn}>
            {apiLoading ? 'Resetting…' : 'Reset Password'}
          </button>
        </form>
      )}
    </AuthHeroLayout>
  )
}
